import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Container,
  Card,
  Row,
  Col,
  Button,
  Spinner,
  Modal,
  Alert,
  Badge,
  ListGroup,
} from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import axios from "axios";
import moment from "moment";
import "react-toastify/dist/ReactToastify.css";

// Use your existing backend base URL
// import { base_url } from "../utils/axiosConfig";
const base_url = "https://health-identity-app.preview.emergentagent.com/";

const EmergencyProfile = () => {
  const { token } = useParams();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const [showPinModal, setShowPinModal] = useState(false);
  const [pin, setPin] = useState("");
  const [pinLoading, setPinLoading] = useState(false);

  const [protectedData, setProtectedData] = useState(null);
  const [showProtectedData, setShowProtectedData] = useState(false);

  const [error, setError] = useState("");

  // ----------------------------------------------------
  // Get Public Emergency Profile
  // ----------------------------------------------------
  const fetchEmergencyProfile = async () => {
    if (!token) {
      setError("Invalid Emergency QR Code.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const response = await axios.get(
        `${base_url}emergency/profile/${encodeURIComponent(token)}`,
      );

      if (response.data?.profile) {
        setProfile(response.data.profile);
      } else {
        setError("Emergency profile is unavailable.");
      }
    } catch (err) {
      console.error("Emergency profile error:", err);

      if (err.response?.status === 404) {
        setError(
          err.response?.data?.detail ||
            "This emergency link is invalid or has been deactivated.",
        );
      } else {
        setError(
          err.response?.data?.detail ||
            "Unable to load the emergency profile. Please try again.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmergencyProfile();
  }, [token]);

  // ----------------------------------------------------
  // Protected Emergency Information
  // ----------------------------------------------------
  const handleProtectedAccess = async () => {
    if (!pin) {
      toast.error("Please enter the Emergency Card PIN.");
      return;
    }

    if (!/^\d{4,6}$/.test(pin)) {
      toast.error("PIN must contain 4 to 6 digits.");
      return;
    }

    try {
      setPinLoading(true);

      const response = await axios.post(
        `${base_url}emergency/protected/${encodeURIComponent(token)}`,
        {
          pin,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (response.data?.protected) {
        setProtectedData(response.data.protected);
        setShowPinModal(false);
        setShowProtectedData(true);
        setPin("");
      } else {
        toast.error("Protected information is unavailable.");
      }
    } catch (err) {
      console.error("Protected emergency access error:", err);

      if (err.response?.status === 429) {
        toast.error(
          err.response?.data?.detail ||
            "Too many incorrect attempts. Please try again later.",
        );
      } else if (err.response?.status === 401) {
        toast.error(
          err.response?.data?.detail || "Access verification failed.",
        );
      } else {
        toast.error(
          err.response?.data?.detail ||
            "Unable to verify the Emergency Card PIN.",
        );
      }
    } finally {
      setPinLoading(false);
    }
  };

  // ----------------------------------------------------
  // Helpers
  // ----------------------------------------------------
  const renderValue = (value) => {
    if (value === null || value === undefined || value === "") {
      return "Not available";
    }

    if (Array.isArray(value)) {
      return value.length ? value.join(", ") : "None";
    }

    return value;
  };

  const renderList = (value) => {
    if (!value) {
      return <span className="text-muted">Not available</span>;
    }

    if (Array.isArray(value)) {
      if (!value.length) {
        return <span className="text-muted">None</span>;
      }

      return (
        <ul className="mb-0 ps-3">
          {value.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
      );
    }

    const values = String(value)
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);

    if (!values.length) {
      return <span className="text-muted">None</span>;
    }

    return (
      <ul className="mb-0 ps-3">
        {values.map((item, index) => (
          <li key={index}>{item}</li>
        ))}
      </ul>
    );
  };

  // ----------------------------------------------------
  // Loading
  // ----------------------------------------------------
  if (loading) {
    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{
          minHeight: "100vh",
          background: "#f8fafc",
        }}
      >
        <div className="text-center">
          <Spinner
            animation="border"
            variant="danger"
            style={{ width: "3rem", height: "3rem" }}
          />

          <h5 className="mt-4 fw-semibold">Loading Emergency Profile...</h5>

          <p className="text-muted">
            Please wait while we retrieve the emergency information.
          </p>
        </div>
      </div>
    );
  }

  // ----------------------------------------------------
  // Error
  // ----------------------------------------------------
  if (error || !profile) {
    return (
      <>
        <div
          className="d-flex align-items-center justify-content-center"
          style={{
            minHeight: "100vh",
            background: "#f8fafc",
            padding: "20px",
          }}
        >
          <Card
            className="border-0 shadow-lg text-center"
            style={{
              maxWidth: "520px",
              width: "100%",
              borderRadius: "20px",
            }}
          >
            <Card.Body className="p-5">
              <div
                className="mx-auto mb-4 d-flex align-items-center justify-content-center"
                style={{
                  width: "90px",
                  height: "90px",
                  borderRadius: "50%",
                  background: "#fee2e2",
                  fontSize: "42px",
                }}
              >
                🚨
              </div>

              <h3 className="fw-bold text-danger">
                Emergency Profile Unavailable
              </h3>

              <p className="text-muted mb-0">
                {error || "This Emergency QR Code is invalid or unavailable."}
              </p>
            </Card.Body>
          </Card>
        </div>

        <ToastContainer />
      </>
    );
  }

  // ----------------------------------------------------
  // Main Emergency Profile
  // ----------------------------------------------------
  return (
    <>
      <div
        style={{
          minHeight: "100vh",
          background:
            "linear-gradient(180deg, #fff7f7 0%, #f8fafc 45%, #ffffff 100%)",
          paddingBottom: "50px",
        }}
      >
        {/* Emergency Header */}
        <div
          style={{
            background: "linear-gradient(135deg, #dc2626, #991b1b)",
            color: "#fff",
            padding: "25px 15px",
            boxShadow: "0 4px 15px rgba(0,0,0,0.12)",
          }}
        >
          <Container>
            <div className="text-center">
              <div
                style={{
                  fontSize: "38px",
                  marginBottom: "5px",
                }}
              >
                🚨
              </div>

              <h2 className="fw-bold mb-1">LIFEONPLUS EMERGENCY PROFILE</h2>

              <p className="mb-0 opacity-75">Emergency information</p>
            </div>
          </Container>
        </div>

        <Container
          style={{
            maxWidth: "850px",
            marginTop: "-10px",
          }}
        >
          {/* Member Profile */}
          <Card
            className="border-0 shadow-lg mb-4"
            style={{
              borderRadius: "20px",
              overflow: "hidden",
            }}
          >
            <Card.Body className="p-4 p-md-5">
              <Row className="align-items-center">
                <Col md={4} className="text-center mb-4 mb-md-0">
                  {profile.photo ? (
                    <img
                      src={profile.photo}
                      alt={profile.name}
                      style={{
                        width: "140px",
                        height: "140px",
                        objectFit: "cover",
                        borderRadius: "50%",
                        border: "5px solid #fee2e2",
                      }}
                    />
                  ) : (
                    <div
                      className="mx-auto d-flex align-items-center justify-content-center"
                      style={{
                        width: "140px",
                        height: "140px",
                        borderRadius: "50%",
                        background: "#fee2e2",
                        fontSize: "60px",
                      }}
                    >
                      👤
                    </div>
                  )}
                </Col>

                <Col md={8}>
                  <div className="text-center text-md-start">
                    <Badge bg="danger" className="mb-2 px-3 py-2">
                      EMERGENCY ACCESS
                    </Badge>

                    <h1 className="fw-bold mb-3">
                      {profile.name || "Unknown"}
                    </h1>

                    <div className="mb-2">
                      <strong>Blood Group:</strong>{" "}
                      <Badge bg="danger">
                        {profile.blood_group || "Not Available"}
                      </Badge>
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Critical Information */}
          <Card
            className="border-0 shadow-sm mb-4"
            style={{ borderRadius: "18px" }}
          >
            <Card.Body className="p-4">
              <h4 className="fw-bold text-danger mb-4">
                ⚠️ Critical Medical Information
              </h4>

              <Row className="g-4">
                <Col md={6}>
                  <div
                    className="h-100 p-4"
                    style={{
                      background: "#fff7ed",
                      borderRadius: "14px",
                      border: "1px solid #fed7aa",
                    }}
                  >
                    <h6 className="fw-bold text-danger mb-3">
                      🩸 Critical Allergies
                    </h6>

                    {renderList(profile.allergies)}
                  </div>
                </Col>

                <Col md={6}>
                  <div
                    className="h-100 p-4"
                    style={{
                      background: "#fef2f2",
                      borderRadius: "14px",
                      border: "1px solid #fecaca",
                    }}
                  >
                    <h6 className="fw-bold text-danger mb-3">
                      🏥 Medical Alerts
                    </h6>

                    {renderList(profile.medical_alerts)}
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Emergency Contacts */}
          <Card
            className="border-0 shadow-sm mb-4"
            style={{ borderRadius: "18px" }}
          >
            <Card.Body className="p-4">
              <h4 className="fw-bold mb-4">📞 Emergency Contacts</h4>

              {profile.emergency_contacts?.length ? (
                <div className="d-flex flex-column gap-3">
                  {profile.emergency_contacts.map((contact, index) => (
                    <Card
                      key={index}
                      className="border"
                      style={{ borderRadius: "14px" }}
                    >
                      <Card.Body>
                        <Row className="align-items-center">
                          <Col xs={8}>
                            <h6 className="fw-bold mb-1">{contact.name}</h6>

                            <p className="text-muted mb-1">
                              {contact.relationship || "Emergency Contact"}
                            </p>

                            <strong>{contact.phone}</strong>
                          </Col>

                          <Col xs={4} className="text-end">
                            <Button
                              variant="success"
                              href={`tel:${contact.phone}`}
                              className="rounded-circle"
                              style={{
                                width: "50px",
                                height: "50px",
                              }}
                            >
                              📞
                            </Button>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              ) : (
                <Alert variant="warning">
                  No emergency contact is available.
                </Alert>
              )}
            </Card.Body>
          </Card>

          {/* Emergency Actions */}
          <Card
            className="border-0 shadow-sm mb-4"
            style={{ borderRadius: "18px" }}
          >
            <Card.Body className="p-4">
              <h4 className="fw-bold mb-4">🚑 Emergency Actions</h4>

              <Row className="g-3">
                <Col md={6}>
                  <Button
                    variant="danger"
                    size="lg"
                    className="w-100 py-3 fw-bold"
                    href={
                      profile.emergency_contacts?.[0]?.phone
                        ? `tel:${profile.emergency_contacts[0].phone}`
                        : undefined
                    }
                    disabled={!profile.emergency_contacts?.length}
                  >
                    📞 Call Emergency Contact
                  </Button>
                </Col>

                <Col md={6}>
                  <Button
                    variant="warning"
                    size="lg"
                    className="w-100 py-3 fw-bold"
                    onClick={() =>
                      toast.info(
                        "Please connect the existing LifeOnPlus ambulance API here.",
                      )
                    }
                  >
                    🚑 Get Ambulance
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Protected Information */}
          <Card
            className="border-0 shadow-lg"
            style={{
              borderRadius: "18px",
              overflow: "hidden",
            }}
          >
            <Card.Body className="p-4 p-md-5 text-center">
              <div style={{ fontSize: "45px" }}>🔐</div>

              <h4 className="fw-bold mt-2">Protected Medical Information</h4>

              <p className="text-muted">
                This information is protected by the Emergency Card PIN.
              </p>

              <Button
                variant="dark"
                size="lg"
                className="px-5 py-3 fw-bold"
                onClick={() => setShowPinModal(true)}
              >
                🔐 Enter Emergency Card PIN
              </Button>
            </Card.Body>
          </Card>

          {/* Security Notice */}
          <div className="text-center mt-4">
            <small className="text-muted">
              🔒 Emergency access is securely monitored by LifeOnPlus.
            </small>
          </div>
        </Container>
      </div>

      {/* ==================================================
          PIN MODAL
      ================================================== */}
      <Modal
        show={showPinModal}
        onHide={() => {
          if (!pinLoading) {
            setShowPinModal(false);
            setPin("");
          }
        }}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton={!pinLoading}>
          <Modal.Title className="fw-bold">🔐 Emergency Card PIN</Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-4">
          <div className="text-center mb-4">
            <div
              style={{
                fontSize: "45px",
              }}
            >
              🔑
            </div>

            <p className="text-muted mb-0">
              Enter the PIN displayed on the member's Emergency Card to view
              protected emergency information.
            </p>
          </div>

          <label className="form-label fw-semibold">Emergency PIN</label>

          <input
            type="password"
            inputMode="numeric"
            autoComplete="off"
            maxLength={6}
            value={pin}
            disabled={pinLoading}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, "").slice(0, 6);

              setPin(value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleProtectedAccess();
              }
            }}
            className="form-control form-control-lg text-center"
            placeholder="Enter 4-6 digit PIN"
            style={{
              letterSpacing: "8px",
              fontSize: "24px",
              fontWeight: "bold",
            }}
          />

          <small className="text-muted d-block mt-2">
            The PIN is verified securely by the LifeOnPlus backend.
          </small>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            disabled={pinLoading}
            onClick={() => {
              setShowPinModal(false);
              setPin("");
            }}
          >
            Cancel
          </Button>

          <Button
            variant="danger"
            disabled={pinLoading || pin.length < 4}
            onClick={handleProtectedAccess}
          >
            {pinLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Verifying...
              </>
            ) : (
              "🔓 Verify PIN"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ==================================================
          PROTECTED INFORMATION MODAL
      ================================================== */}
      <Modal
        show={showProtectedData}
        onHide={() => setShowProtectedData(false)}
        centered
        size="lg"
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold">
            🔐 Protected Emergency Information
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-4">
          {protectedData && (
            <>
              {/* Basic Information */}
              <Card className="border-0 bg-light mb-4">
                <Card.Body>
                  <h5 className="fw-bold mb-3">👤 Personal Information</h5>

                  <Row className="g-3">
                    <Col md={6}>
                      <strong>LifeOnPlus ID</strong>
                      <div>{renderValue(protectedData.lifeonplus_id)}</div>
                    </Col>

                    <Col md={6}>
                      <strong>Date of Birth</strong>
                      <div>{renderValue(protectedData.dob)}</div>
                    </Col>

                    <Col md={6}>
                      <strong>Gender</strong>
                      <div>{renderValue(protectedData.gender)}</div>
                    </Col>

                    <Col md={6}>
                      <strong>Mobile</strong>
                      <div>{renderValue(protectedData.mobile)}</div>
                    </Col>

                    <Col md={6}>
                      <strong>Height</strong>
                      <div>{renderValue(protectedData.height)}</div>
                    </Col>

                    <Col md={6}>
                      <strong>Weight</strong>
                      <div>{renderValue(protectedData.weight)}</div>
                    </Col>

                    <Col md={6}>
                      <strong>Blood Group</strong>
                      <div>{renderValue(protectedData.blood_group)}</div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Medical Information */}
              <Card className="border-0 bg-light mb-4">
                <Card.Body>
                  <h5 className="fw-bold mb-3">🏥 Medical Information</h5>

                  <div className="mb-3">
                    <strong>Allergies</strong>
                    <div>{renderList(protectedData.allergies)}</div>
                  </div>

                  <div className="mb-3">
                    <strong>Conditions</strong>
                    <div>{renderList(protectedData.conditions)}</div>
                  </div>

                  <div>
                    <strong>Medications</strong>

                    {protectedData.medications?.length ? (
                      <ListGroup className="mt-2">
                        {protectedData.medications.map((medicine, index) => (
                          <ListGroup.Item key={index}>
                            💊 {medicine}
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    ) : (
                      <div className="text-muted mt-1">
                        No medication information available.
                      </div>
                    )}
                  </div>
                </Card.Body>
              </Card>

              {/* Medical Records */}
              {protectedData.records?.length > 0 && (
                <Card className="border-0 bg-light">
                  <Card.Body>
                    <h5 className="fw-bold mb-3">📄 Medical Records</h5>

                    <div className="d-flex flex-column gap-3">
                      {protectedData.records.map((record, index) => (
                        <Card key={index} className="border">
                          <Card.Body>
                            <h6 className="fw-bold mb-1">
                              {record.title || "Medical Record"}
                            </h6>

                            {record.category && (
                              <Badge bg="secondary" className="me-2">
                                {record.category}
                              </Badge>
                            )}

                            {record.record_date && (
                              <div className="small text-muted mt-2">
                                Date:{" "}
                                {moment(record.record_date).format(
                                  "DD MMM YYYY",
                                )}
                              </div>
                            )}

                            {record.notes && (
                              <p className="mb-0 mt-2">{record.notes}</p>
                            )}
                          </Card.Body>
                        </Card>
                      ))}
                    </div>
                  </Card.Body>
                </Card>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowProtectedData(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer position="top-right" />
    </>
  );
};

export default EmergencyProfile;
